import React, { useState, useEffect } from "react";
import { 
  Box, 
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Tooltip
} from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelectedLadderOrdersStore } from "../../Store/selectedLadderOrdersStore";
import { useAuth } from "../../Context/AuthProvider";
import { useNavigate } from "react-router-dom";
import SelectedOrderTableRow from './SelectedOrderTableRow';
import { calculateOrderFinancials } from '../../Utils/ladderCaclulations';
// NEW
import { tokens, withAlpha } from "../../Common/designTokens";

const SelectedOrdersPreviewTable = ({
  selectedColumns,
  selectedRung,
  bondLadderId,
  ladderData,
  showPreviewOnly = false,
  maxTableHeight = 190, // New prop with default value of 239px
  calculateTotalCost = false, // New prop
  onRowClick, // New prop for handling row clicks
  faceValue = 1, // Added faceValue prop with default of 1
  pendingRequests = {}, // Added pendingRequests prop
  pendingCalcUUIDs = [], // Added pendingCalcUUIDs prop
  timeoutMessages = {}, // Add new prop for timeout messages
  removeOrder, // Make sure this prop is included
  orderError = '', // Add orderError prop
  // NEW: follow parent container height and disable height transition while dragging
  fitParentHeight = false,
  disableHeightTransition = false,
}) => {
  const { token } = useAuth();
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const { selectedOrders } = useSelectedLadderOrdersStore();
  const [tableHeight, setTableHeight] = useState('auto');
  const navigate = useNavigate();
  const selectedOrdersArray = Object.entries(selectedOrders).map(([rungIndex, order]) => ({
    ...order,
    rungIndex,
  }));

  // Process the data to add the calculated total cost field and calculate qty
  const processedOrdersArray = selectedOrdersArray.map((order) => {
    // Get the amount per rung from ladder data (used by automatic calculation)
    const amountPerRung = parseFloat(ladderData?.amountPerRung) || parseFloat(order.qty) || 0;

    // Use the shared utility function to get costPerUnit and defaults
    const calc = calculateOrderFinancials(order, amountPerRung, faceValue);
    const costPerUnit = calc.costPerUnit || 0;

    // If the user manually set qty, respect that value and compute totalCost accordingly
    if (order.manualQty && typeof order.qty !== 'undefined' && order.qty !== null) {
      const manualQty = Number(order.qty) || 0;
      const totalCost = costPerUnit * manualQty;
      return {
        ...order,
        qty: manualQty,
        originalAmtPerRung: amountPerRung,
        totalCost
      };
    }

    // Otherwise use calculated qty/totalCost
    const { qty, totalCost } = calc;
    return {
      ...order,
      qty, // Use calculated and rounded qty
      originalAmtPerRung: amountPerRung, // Keep original amount for reference
      totalCost // Add calculated total cost using shared formula
    };
  });

  // Constants for height calculation with improved safety margins
  const TABLE_HEADER_HEIGHT = 40;      // Table header row height
  const ROW_HEIGHT = 40;               // Height of each data row
  const ROW_SAFETY_MARGIN = showPreviewOnly ? 0 : 10;  // Set to 0 when in BondLadderPreview
  const PADDING = 16;                  // Additional padding
  const MIN_TABLE_HEIGHT = 100;        // Minimum height with at least one row
  const SCROLLBAR_HEIGHT = 8;          // Scrollbar height for horizontal scrolling

  // Define a consistent background color for the entire table
  // CHANGED to tokens (light theme)
  const TABLE_BG_COLOR = tokens.colors.defaultGridRowColor;
  const TABLE_HEADER_BG_COLOR = tokens.colors.defaultGridHeaderBackground;
  const ROW_HIGHLIGHT_COLOR = tokens.colors.defaultGridHoverColor;

  // Improved height calculation algorithm
  useEffect(() => {
    // Skip JS height calculation when fitting parent; pure CSS (100%) instead
    if (fitParentHeight) {
      setTableHeight('100%');
      return;
    }
    const rowCount = selectedOrdersArray.length;
    if (rowCount === 0) {
      setTableHeight('0px');
      return;
    }

    // New formula with safety margin per row
    const calculatedRowsHeight = rowCount * (ROW_HEIGHT + ROW_SAFETY_MARGIN);
    const calculatedHeight = TABLE_HEADER_HEIGHT + calculatedRowsHeight + PADDING;
    
    // If maxTableHeight is null or 0, don't impose a maximum height
    if (maxTableHeight === 0 || maxTableHeight === null || maxTableHeight === undefined) {
      setTableHeight(`${Math.max(MIN_TABLE_HEIGHT, calculatedHeight)}px`);
      return;
    }
    
    // Otherwise, clamp to min/max values
    const finalHeight = Math.max(MIN_TABLE_HEIGHT, Math.min(calculatedHeight, maxTableHeight));
    setTableHeight(`${finalHeight}px`);
  }, [selectedOrdersArray.length, maxTableHeight, fitParentHeight]);

  const formatValue = (value, columnKey) => {
    if (value === undefined || value === null || value === "") return "---";
    
    // Helper function to format dates as DDMonYY with optional time
    const formatDateToDDMonYY = (dateStr, showTime = true) => {
      try {
        // Ensure timestamp is treated as UTC by appending 'Z' if not present
        let valueStr = dateStr.toString();
        if (!valueStr.endsWith('Z')) {
          valueStr += 'Z';
        }
        
        const date = new Date(valueStr);
        if (isNaN(date.getTime())) return "---";
        
        const day = date.getUTCDate();
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getUTCFullYear().toString().slice(-2);
        
        // Add time in 24-hour format only if showTime is true
        if (showTime) {
          const hours = date.getUTCHours().toString().padStart(2, '0');
          const minutes = date.getUTCMinutes().toString().padStart(2, '0');
          return `${day}${month}${year} ${hours}:${minutes}`;
        }
        
        // Return only the date portion
        return `${day}${month}${year}`;
      } catch (e) {
        console.error("Error formatting date:", e, "for value:", dateStr);
        return "---";
      }
    };
    
    if (columnKey === "insured" || columnKey === "altMinTax") {
      return value === 1 ? "Yes" : value === 0 ? "No" : "---";
    }
    
    const numericColumns = [
      "qty", "quantity", "price", "minQty", "maxQty", "ytm", "ytw1",
      "accruedInterest", "estimatedTotal", "duration", "coupon",
      "executedQuantity", "executedAveragePrice", "numberOfRungs",
      "amountPerRung", "totalMaturity", "totalCost", "poolFactor"
    ];
    
    if (numericColumns.includes(columnKey)) {
      if (typeof value === "number" || !isNaN(Number(value))) {
        const num = Number(value);
        
        // Match BondTable's approach for which columns get decimal places
        const twoDecimalPlaces = ["price", "accruedInterest", "estimatedTotal", 
                                 "executedAveragePrice", "coupon", "totalCost", "ytm", "poolFactor"];
        const decimalPlaces = twoDecimalPlaces.includes(columnKey) ? 2 : 0;
        
        // Format with comma separators for thousands and appropriate decimal places
        return num.toLocaleString("en-US", {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        });
      }
      return "---";
    }
    
    const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime"];
    if (dateColumns.includes(columnKey) && value) {
      // For maturity column, don't show time
      if (columnKey === "maturity") {
        return formatDateToDDMonYY(value, false);
      }
      return formatDateToDDMonYY(value);
    }
    return value;
  };

  const isNumericOrDate = (columnKey) => {
    const numericColumns = [
      "qty", "quantity", "price", "minQty", "maxQty", "ytc", "ytw1","ytm",
      "accruedInterest", "estimatedTotal", "duration", "coupon",
      "executedQuantity", "executedAveragePrice", "numberOfRungs",
      "amountPerRung", "totalMaturity", "altMinTax", "insured", "ytc", "totalCost", "poolFactor" // Added totalCost here
    ];
    const dateColumns = ["maturity", "nextCallDate", "createdAt", "dateTime"];
    return numericColumns.includes(columnKey) || dateColumns.includes(columnKey);
  };

  const isBuySellColumn = (columnKey) => columnKey === "side" || columnKey === "action";
  const isTypeLikeColumn = (columnKey) => ["type", "bondType", "bondSubType"].includes(columnKey);
  const needsTooltip = (columnKey) => ["description", "bondType", "bondSubType", "sector", "incomeSource"].includes(columnKey);
  const getColumnMaxWidth = (columnKey) => {
    if (columnKey === "description") return "200px";
    if (isNumericOrDate(columnKey)) return "90px";
    if (columnKey === "cusip") return "100px";
    if (isTypeLikeColumn(columnKey)) return "120px";
    return "150px";
  };

  const handleCloseNotification = () => setNotification({ ...notification, open: false });

  const rowHeight = "40px";
  const cellPadding = "4px 8px";

  // Add a cell renderer that makes comments clickable
  const cellRenderer = (row, column, formattedValue) => {
    // Get original value for special handling (like comments)
    const value = row[column.key];
    const isEditable = row.isEditable !== false;
    
    // Style for clickable cells
    const clickableStyle = {
      cursor: 'pointer',
      textDecoration: 'underline',
      color: tokens.palette.primary, // CHANGED
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: "100%",
      display: "block"
    };
    
    // Style for non-clickable cells (when not editable)
    const nonClickableStyle = {
      cursor: 'default',
      color: tokens.colors.fontColour50, // CHANGED
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: "100%",
      display: "block"
    };

    // Helper: render qty with warning icon when qty < minQty on preview page
    if (column.key === 'qty') {
      const qtyNum = Number(row.qty) || 0;
      const minQtyNum = Number(row.minQty) || 0;
      const showQtyWarning = showPreviewOnly && minQtyNum > 0 && qtyNum >= 0 && qtyNum < minQtyNum;

      const content = (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 6,
            width: '100%'
          }}
          // Native tooltip like parent BondTable
          title={formattedValue}
        >
          <span
            style={{
              display: 'inline-block',
              textAlign: 'right',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {formattedValue}
          </span>
          {showQtyWarning && (
            <Tooltip
              title={`Quantity is less than min qty (${minQtyNum.toLocaleString('en-US')})`}
              arrow
              placement="top"
            >
              <InfoOutlinedIcon
                sx={{
                  mt: '2px',
                  fontSize: 14,
                  color: tokens.colors.fontColour, // CHANGED
                  verticalAlign: 'middle',
                }}
              />
            </Tooltip>
          )}
        </div>
      );

      if (onRowClick && isEditable) {
        return (
          <div
            style={clickableStyle}
            onClick={() => onRowClick(row, row.rungIndex)}
            title={formattedValue}
          >
            {content}
          </div>
        );
      }
      return <div style={nonClickableStyle} title={formattedValue}>{content}</div>;
    }

    // Make comments and price cells clickable only if editable
    if ((column.key === 'comments' || column.key === 'price') && onRowClick && isEditable) {
      return (
        <div
          style={clickableStyle}
          onClick={() => onRowClick(row, row.rungIndex)}
          title={column.key === 'comments' ? (value || 'Add comment') : formattedValue}
        >
          {column.key === 'comments' ? (value || 'Add comment') : formattedValue}
        </div>
      );
    }
    
    // Show disabled style for non-editable comments/price
    if ((column.key === 'comments' || column.key === 'price') && !isEditable) {
      return (
        <div
          style={nonClickableStyle}
          title={column.key === 'comments' ? (value || 'N/A') : formattedValue}
        >
          {column.key === 'comments' ? (value || 'N/A') : formattedValue}
        </div>
      );
    }
    
    // Default rendering for other cells - use the formatted value
    return formattedValue;
  };

  // Define styles object to pass to TableRow
  const styles = {
    rowHeight,
    cellPadding,
    TABLE_BG_COLOR,
    ROW_HIGHLIGHT_COLOR
  };

  // compute total portfolio cost from processedOrdersArray (honors manualQty)
  const totalPortfolioCost = processedOrdersArray.reduce((sum, o) => sum + (Number(o.totalCost) || 0), 0);

  return (
    <>
      <Box sx={{
        height: tableHeight,
        width: 'calc(100% - 20px)',
        transition: disableHeightTransition ? 'none' : 'height 0.2s ease',
        overflow: 'hidden',
        backgroundColor: TABLE_BG_COLOR,
        mx: '10px',
      }}>
        <Box sx={{
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: TABLE_BG_COLOR,
          minWidth: 0,
          height: '100%',
        }}>
          <TableContainer 
            component={Paper}
            sx={{
              position: 'relative',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              height: '100%',
              maxHeight: '100%',
              overflowX: 'auto',
              overflowY: 'auto',
              backgroundColor: TABLE_BG_COLOR,
              boxShadow: 'none',
              borderRadius: '0px', // CHANGED
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
                display: 'block',
              },
              '&::-webkit-scrollbar-track': {
                background: tokens.colors.scrollbarTrack, // CHANGED
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: tokens.colors.scrollbarThumb, // CHANGED
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: withAlpha(tokens.colors.scrollbarThumb, 0.3), // CHANGED
                },
              },
              scrollbarWidth: 'thin',
              scrollbarColor: `${tokens.colors.scrollbarThumb} ${tokens.colors.scrollbarTrack}`, // CHANGED
            }}
          >
            <Table 
              stickyHeader
              size="small" 
              sx={{ 
                backgroundColor: TABLE_BG_COLOR,
                minWidth: showPreviewOnly ? '100%' : '800px',
                tableLayout: 'auto',
              }}
            >
              <TableHead>
                <TableRow>
                  {!showPreviewOnly && (
                    <TableCell
                      sx={{
                        backgroundColor: TABLE_HEADER_BG_COLOR,
                        color: tokens.colors.headerFontColour, // CHANGED
                        padding: cellPadding,
                        fontWeight: 700,
                        height: rowHeight,
                        userSelect: "none",
                        borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
                        borderRight: `1px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
                        width: "60px",
                        textAlign: "center",
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        zIndex: 10,
                      }}
                    >
                      Delete
                    </TableCell>
                  )}
                  
                  <TableCell
                    sx={{
                      backgroundColor: TABLE_HEADER_BG_COLOR,
                      color: tokens.colors.headerFontColour, // CHANGED
                      padding: cellPadding,
                      fontWeight: 700,
                      height: rowHeight,
                      userSelect: "none",
                      borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
                      borderRight: `1px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
                      width: "60px",
                      textAlign: "right",
                      position: 'sticky',
                      top: 0,
                      zIndex: 5,
                      ...(showPreviewOnly && { left: 0 }),
                    }}
                  >
                    Rung
                  </TableCell>
                  
                  {selectedColumns
                    .filter(col => !["minQty", "maxQty"].includes(col.key))
                    .map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{
                          backgroundColor: TABLE_HEADER_BG_COLOR,
                          color: tokens.colors.headerFontColour, // CHANGED
                          padding: cellPadding,
                          fontWeight: 700,
                          height: rowHeight,
                          userSelect: "none",
                          borderBottom: `2px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
                          borderRight: `1px solid ${tokens.colors.defaultGridBorderColor}`, // CHANGED
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          textAlign: isNumericOrDate(column.key) ? "right" : "left",
                          minWidth: isNumericOrDate(column.key) ? "90px" : "120px",
                          maxWidth: getColumnMaxWidth(column.key),
                          position: 'sticky',
                          top: 0,
                          zIndex: 5,
                        }}
                      >
                        {column.name}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody sx={{ backgroundColor: TABLE_BG_COLOR }}>
                {processedOrdersArray.map((rowData) => (
                  <SelectedOrderTableRow
                    key={`preview-${rowData.cusip}-${rowData.rungIndex}`}
                    rowData={rowData}
                    selectedColumns={selectedColumns}
                    selectedRung={selectedRung}
                    showPreviewOnly={showPreviewOnly}
                    onRowClick={onRowClick}
                    removeOrder={removeOrder} // Pass the removeOrder function from props
                    formatValue={formatValue}
                    isNumericOrDate={isNumericOrDate}
                    isBuySellColumn={isBuySellColumn}
                    isTypeLikeColumn={isTypeLikeColumn}
                    getColumnMaxWidth={getColumnMaxWidth}
                    cellRenderer={cellRenderer}
                    styles={styles}
                    pendingRequests={pendingRequests} // Forward pendingRequests prop
                    pendingCalcUUIDs={pendingCalcUUIDs} // Forward pendingCalcUUIDs prop
                    timeoutMessages={timeoutMessages} // Forward timeoutMessages prop
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SelectedOrdersPreviewTable;
